import { Routes, Route, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Layers, Brain, BarChart3, Wrench, User, LogIn, Sparkles, Clock } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import Dashboard from './pages/Dashboard.jsx';
import AdminPage from './pages/AdminPage.jsx';
import PoolPage from './pages/PoolPage.jsx';
import PickPage from './pages/PickPage.jsx';
import TrendPage from './pages/TrendPage.jsx';
import ToolsPage from './pages/ToolsPage.jsx';
import BacktestPage from './pages/BacktestPage.jsx';
import SupportPage from './pages/SupportPage.jsx';
import SettingsPage from './pages/SettingsPage.jsx';
import SocialHubPage from './pages/SocialHubPage.jsx';
import ProfilePage from './pages/ProfilePage.jsx';
import MyPicksPage from './pages/MyPicksPage.jsx';
import ChatWidget from './components/ChatWidget.jsx';
import ThemeToggle from './components/ThemeToggle.jsx';
// 强制只用 dark 模式,ThemeToggle 已被禁用(下面改成静态)
import LoginModal from './components/LoginModal.jsx';
import { RenewalNudgeModal, useRenewalNudge } from './components/RenewalNudgeModal.jsx';
import { SmartNudge } from './components/SmartNudge.jsx';
import { useHistoryStore } from './stores/history.js';
import { next as baseNext } from './data/history.js';
import { api } from './lib/api.js';
import { useUser } from './stores/user.js';

const nav = [
  { to: '/', label: '首页', icon: Home },
  { to: '/pool', label: '大底池', icon: Layers, badge: 'NEW' },
  { to: '/pick', label: '选号', icon: Brain },
  { to: '/trend', label: '走势', icon: BarChart3 },
  { to: '/tools', label: '工具', icon: Wrench },
  { to: '/profile', label: '我的', icon: User },
];

// 底部 Tab Bar(移动端)
function BottomTabBar() {
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden glass-strong border-t border-white/10 safe-area-bottom">
      <div className="grid grid-cols-6 max-w-lg mx-auto">
        {nav.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 py-2.5 transition-all relative ${
                isActive ? 'text-brand-cyan' : 'text-ink-3'
              }`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.div
                    layoutId="bottomTab"
                    className="absolute -top-0.5 w-8 h-0.5 rounded-full bg-gradient-to-r from-brand-purple to-brand-cyan"
                  />
                )}
                <item.icon size={18} />
                <span className="text-[10px] font-medium leading-none mt-0.5">
                  {item.label}
                </span>
                {item.badge && (
                  <span className="absolute top-0.5 right-1.5 w-1 h-1 rounded-full bg-brand-rose" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

// 顶部 Header(桌面完整 + 移动简化)
function Header() {
  const history = useHistoryStore(s => s.history);
  const last = history[0];
  // 目标期 = 最新期 + 1(用真实数据,而不是 baseNext)
  const targetPeriod = last?.p ? String(parseInt(last.p) + 1).slice(-3) : baseNext.period.slice(-3);
  const lastThree = last?.p ? last.p.slice(-3) : '';
  // 隐藏入口:点 logo 5 次进后台(只有开发者知道)
  const [logoClicks, setLogoClicks] = useState(0);
  const logoClickTimer = useRef(null);
  const handleLogoClick = (e) => {
    e.preventDefault();
    setLogoClicks(c => {
      const newC = c + 1;
      if (newC >= 5) {
        // 5 次点击进后台
        if (logoClickTimer.current) clearTimeout(logoClickTimer.current);
        setLogoClicks(0);
        // 如果 localStorage 已有自定义 token,保留;否则用临时 token 让用户能进登录页
        if (!localStorage.getItem('fucai3d_admin_token')) {
          localStorage.setItem('fucai3d_admin_token', 'admin2026');
        }
        if (typeof window !== 'undefined' && window.history) {
          window.history.pushState({}, '', '/admin');
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
        toast.success('进入后台');
        return 0;
      }
      // 2 秒后重置
      if (logoClickTimer.current) clearTimeout(logoClickTimer.current);
      logoClickTimer.current = setTimeout(() => setLogoClicks(0), 2000);
      // 提示进度
      if (newC === 2) toast('再点 3 次进入后台', { icon: '🤫', duration: 800 });
      if (newC === 3) toast('再点 2 次', { icon: '🤫', duration: 800 });
      if (newC === 4) toast('再点 1 次!', { icon: '🔓', duration: 800 });
      return newC;
    });
  };
  return (
    <header className="sticky top-0 z-40 glass-strong border-b border-white/5">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-6">
        <NavLink to="/" onClick={handleLogoClick} className="flex items-center gap-2 group flex-shrink-0">
          <div className="relative w-8 h-8 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform overflow-hidden" style={{ background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #b45309 100%)' }}>
            <span className="text-base sm:text-lg relative z-10">🐝</span>
            <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'repeating-linear-gradient(60deg, transparent, transparent 4px, rgba(0,0,0,0.15) 4px, rgba(0,0,0,0.15) 6px)' }}></div>
          </div>
          <div className="hidden sm:block">
            <div className="font-bold text-ink-0 leading-none text-sm">蜜蜂空间</div>
            <div className="text-[10px] text-ink-3 leading-none mt-0.5">数据采集 · 智能选号 · 大底池</div>
          </div>
        </NavLink>

        <nav className="hidden md:flex items-center gap-1 flex-1">
          {nav.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all relative ${
                  isActive
                    ? 'bg-brand-purple/15 text-brand-purple border border-brand-purple/30'
                    : 'text-ink-2 hover:text-ink-0 hover:bg-white/5'
                }`
              }
            >
              <item.icon size={14} />
              <span>{item.label}</span>
              {item.badge && (
                <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-brand-rose/20 text-brand-rose">
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-3 text-sm ml-auto">
          <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-md sm:rounded-lg bg-white/5 border border-white/10">
            <span className="text-[10px] sm:text-xs text-ink-3">目标</span>
            <span className="font-mono font-semibold text-brand-cyan text-[11px] sm:text-sm">{targetPeriod}</span>
          </div>
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
            <span className="text-xs text-ink-3">上期</span>
            <span className="font-mono font-semibold text-brand-gold">
              {last.a}{last.b}{last.c}
            </span>
          </div>
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/5 border border-white/10"
            title="深色模式(品牌主色)"
          >
            <Sparkles size={16} className="text-brand-gold" />
          </div>
        </div>
      </div>
    </header>
  );
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, setUser } = useUser();
  const [showInstall, setShowInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showLogin, setShowLogin] = useState(false);

  // 全局轮询 user(每 5s,任何页面都生效 — 客户付款/补单后无需刷新)
  useEffect(() => {
    if (!token) return;
    let inFlight = false;
    const sync = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const r = await api.request('/api/auth/me', { skipAuth: true });
        const me = r?.user;
        if (me && user && (me.tier !== user.tier || me.tier_expires_at !== user.tier_expires_at)) {
          setUser({ ...user, ...me });
          console.log(`[App user] 全局同步: ${user.tier} → ${me.tier}`);
        }
      } catch {} finally { inFlight = false; }
    };
    sync();
    const t = setInterval(sync, 5000);
    return () => clearInterval(t);
  }, [token, user?.tier, user?.tier_expires_at]);
  // 启动时:如果没登录,自动弹登录
  useEffect(() => {
    if (!user && !token && location.pathname !== '/admin') {
      // 延后一点点,避免 modal 与路由切换冲突
      const t = setTimeout(() => setShowLogin(true), 400);
      return () => clearTimeout(t);
    }
  }, [user, token, location.pathname]);

  // 试用过期拦截(MAX v7 完全锁):强制跳到升级页
  //   - 试用中(未过期):不锁
  //   - 付费会员(tier_expires_at > now):不锁
  //   - 永久买断(tier = 'MAX 买断'):不锁
  //   - 未登录:不锁(让登录)
  //   - admin 后台:不锁
  //   - 升级页(/profile):不锁(避免循环)
  useEffect(() => {
    if (!user) return;
    if (location.pathname.startsWith('/admin')) return;
    if (location.pathname === '/profile') return;

    const now = Date.now();
    const isOnTrial = user.trial_expires_at && user.trial_expires_at > now;
    const isPaid = user.tier_expires_at && user.tier_expires_at > now;
    // 任何带"买断"或"MAX"的 tier 视为永久
    const isPermanent = user.tier && (
      user.tier.includes('买断') ||
      user.tier === 'MAX 买断' ||
      user.tier === 'Pro 永久' ||
      user.tier === '永久买断'
    );

    // 试用中 → 不锁
    if (isOnTrial) return;
    // 已付费 → 不锁
    if (isPaid || isPermanent) return;

    // 否则:已过期/无权限 → 跳升级页
    // 给一点延迟,让用户先看到主页(增强体验)
    const t = setTimeout(() => {
      navigate('/profile', { replace: true, state: { reason: 'trial_expired' } });
    }, 800);
    return () => clearTimeout(t);
  }, [user, location.pathname, navigate]);

  // 监听 PWA 安装提示
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // 启动时拉后端最新 3D 历史数据(MAX v7: 用 3D 端点覆盖本地 baseHistory)
  useEffect(() => {
    let cancelled = false;
    let inFlight = false;  // 防 race condition
    async function fetchAndMerge() {
      if (inFlight) return;  // 上次还在跑,跳过
      inFlight = true;
      try {
        const API_BASE = (typeof window !== 'undefined' && window.location.hostname === '106.53.41.207') ? '' : 'https://api.3dmfkj.top';
        const r = await fetch(`${API_BASE}/api/3d/history?N=120`).then(r => r.json());
        if (cancelled || !r?.ok || !r.data || r.data.length === 0) return;
        // 转换格式(同时保留 sum/span/type 全名,兼容公式)
        const fresh = r.data.map(x => ({
          p: x.p,
          date: x.created_at || x.date,
          a: x.a, b: x.b, c: x.c,
          sum: x.sum, span: x.span, type: x.type,
          s: x.sum, sp: x.span, t: x.type,
        }));
        // 合并到 history store
        const { useHistoryStore } = await import('./stores/history.js');
        const { history: cur } = useHistoryStore.getState();
        const oldLatest = cur[0]?.p;
        const merged = [...fresh, ...cur].reduce((acc, x) => {
          if (!acc.find(y => y.p === x.p)) acc.push(x);
          return acc;
        }, []).sort((a, b) => parseInt(b.p) - parseInt(a.p));
        useHistoryStore.setState({ history: merged, updatedAt: Date.now() });
        const newLatest = merged[0]?.p;
        if (oldLatest && newLatest && newLatest !== oldLatest) {
          console.log(`[App 3D] ✨ 新一期开奖! ${oldLatest} → ${newLatest}`);
          // 触发大底池 / 走势 / 选号 重渲染
          window.dispatchEvent(new CustomEvent('3d:new-draw', { detail: { period: newLatest } }));
        } else {
          console.log(`[App 3D] 同步最新历史: ${fresh.length} 期 (最新: ${fresh[0]?.p})`);
        }
      } catch (e) {
        console.warn('[App 3D] 拉最新历史失败:', e.message);
      }
    }
    // 立即跑一次
    fetchAndMerge();
    // 每 60s 轮询(带 inFlight 锁,防 race condition)
    const timer = setInterval(fetchAndMerge, 60000);
    return () => { cancelled = true; clearInterval(timer); inFlight = false; };
  }, []);

  const installPWA = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowInstall(false);
    }
    setDeferredPrompt(null);
  };

  // 后台路由:全屏,不带外层 Header/TabBar/Footer
  if (location.pathname.startsWith('/admin')) {
    return (
      <div className="min-h-screen flex flex-col bg-bg-base">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="flex-1"
          >
            <Routes location={location}>
              <Route path="/admin" element={<AdminPage />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-bg-base">
      <Header />

      {/* 智能命中提醒(收藏底池命中时弹) */}
      <SmartNudge />

      {/* 试用过期软拦截(顶部 banner,允许继续浏览但提示升级) */}
      {user && user.tier_expires_at !== null && user.tier_expires_at !== undefined && user.tier_expires_at < Date.now() && location.pathname !== '/admin' && (
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="sticky top-14 md:top-16 z-20 px-3 sm:px-4 py-2"
          style={{ background: 'linear-gradient(90deg, rgba(239,68,68,0.15) 0%, rgba(251,113,133,0.1) 100%)' }}
        >
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-xs">
            <Clock size={14} className="text-accent-danger flex-shrink-0" />
            <span className="flex-1">
              试用已过期 · 部分 Pro 功能受限 · <span className="text-ink-2">基础功能仍可使用</span>
            </span>
            <Link
              to="/profile"
              className="px-2 py-1 rounded bg-accent-danger/20 border border-accent-danger/40 text-accent-danger text-[10px] font-semibold flex-shrink-0"
            >
              立即升级 →
            </Link>
          </div>
        </motion.div>
      )}

      {/* 试用中 banner(还剩 N 小时) */}
      {user && user.trial_expires_at && user.trial_expires_at > Date.now() && !user.tier_expires_at && location.pathname !== '/admin' && (
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="sticky top-14 md:top-16 z-20 px-3 sm:px-4 py-2"
          style={{ background: 'linear-gradient(90deg, rgba(251,191,36,0.15) 0%, rgba(168,85,247,0.08) 100%)' }}
        >
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-xs">
            <Sparkles size={14} className="text-brand-gold flex-shrink-0" />
            <span className="flex-1">
              🎁 试用中 · 还剩 <span className="text-brand-gold font-bold">{Math.max(0, Math.ceil((user.trial_expires_at - Date.now()) / 3600000))} 小时</span>
            </span>
            <Link
              to="/profile"
              className="px-2 py-1 rounded bg-brand-gold/20 border border-brand-gold/40 text-brand-gold text-[10px] font-semibold flex-shrink-0"
            >
              升级续期 →
            </Link>
          </div>
        </motion.div>
      )}

      {/* 未登录拦截 — 软拦截:显示半透明遮罩,不阻断体验 */}
      {!user && location.pathname !== '/admin' && !showLogin && (
        <div className="fixed bottom-20 md:bottom-4 left-3 right-3 md:left-auto md:right-4 md:w-96 z-30">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="card text-center"
            style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.15) 0%, rgba(168,85,247,0.1) 100%)' }}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-cyan to-brand-purple flex items-center justify-center flex-shrink-0">
                <LogIn size={18} className="text-white" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm font-bold">注册 / 登录后使用</div>
                <div className="text-[10px] text-ink-2 mt-0.5">
                  首次注册自动获得 <span className="text-brand-gold font-semibold">24 小时 Pro 试用</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowLogin(true)}
                className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-brand-purple to-brand-cyan text-white text-[11px] font-semibold flex items-center gap-1 flex-shrink-0"
              >
                立即注册 →
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <main className="flex-1 max-w-7xl w-full mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-24 md:pb-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <Routes location={location}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/pool" element={<PoolPage />} />
              <Route path="/pick" element={<PickPage />} />
              <Route path="/trend" element={<TrendPage />} />
              <Route path="/tools" element={<ToolsPage />} />
              <Route path="/backtest" element={<BacktestPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/social" element={<SocialHubPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/picks" element={<MyPicksPage />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </main>

      {/* PWA 安装提示(Android Chrome 才有) */}
      {showInstall && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-20 md:bottom-4 left-3 right-3 md:left-auto md:right-4 md:w-80 z-50"
        >
          <div className="card border-amber-500/40 flex items-center gap-3 shadow-2xl shadow-amber-500/20" style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.08) 0%, rgba(180,83,9,0.05) 100%)' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0" style={{ background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #b45309 100%)' }}>
              🐝
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">蜜蜂空间 · 装到桌面</div>
              <div className="text-[10px] text-ink-3">像 APP 一样使用,离线可用</div>
            </div>
            <button
              onClick={installPWA}
              className="btn-primary text-xs px-3 py-1.5"
            >
              安装
            </button>
            <button
              onClick={() => setShowInstall(false)}
              className="text-ink-3 text-xs px-1"
            >
              ✕
            </button>
          </div>
        </motion.div>
      )}

      <BottomTabBar />

      <footer className="hidden md:block border-t border-white/5 py-4 mt-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-xs text-ink-3">
          <span>蜜蜂空间 · 3D 选号分析工具 · </span>
          <span className="text-ink-2">数据驱动 · 智能选号 · 大底池层层筛选</span>
          <span className="mx-2">·</span>
          <span className="text-ink-3">数据来源 500.com / 17500.cn</span>
          <span className="mx-2">·</span>
          <span className="text-accent-warn">⚠ 仅供研究参考,理性投注</span>
        </div>
      </footer>

      {/* 全站浮动在线客服按钮 */}
      <ChatWidget />

      {/* 登录 Modal — 强制登录用 */}
      <AnimatePresence>
        {showLogin && (
          <LoginModal
            open={showLogin}
            onClose={() => setShowLogin(false)}
            onSuccess={() => {
              setShowLogin(false);
              // 登录成功后,自动给 24h Pro 试用(后端 login 已实现)
            }}
          />
        )}
      </AnimatePresence>

      {/* 续费倒计时挽留(Pro 到期前 3/1 天弹) */}
      <RenewalNudgeModalWrapper />
    </div>
  );
}

// 续费挽留 modal wrapper(用 hook 控制)
function RenewalNudgeModalWrapper() {
  const { show, close } = useRenewalNudge();
  return show ? (
    <RenewalNudgeModal
      onClose={close}
      onUpgrade={(tier, amount) => {
        close();
        // 跳到 profile 页(让用户点升级按钮)
        window.location.hash = '#/profile';
        setTimeout(() => {
          const el = document.querySelector('[data-upgrade-tier="' + tier + '"]');
          if (el) el.click();
        }, 300);
      }}
    />
  ) : null;
}
